import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useOnboarding, useDismissible } from "@safers/pwa";
import { AppHandoffScreen } from "@/components/AppHandoffScreen";
import { SiteUnavailableScreen } from "@/components/SiteUnavailableScreen";
import { OnboardingCard } from "@/components/OnboardingCard";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { useWorkerNotices } from "@/hooks/useWorkerNotices";
import { parseCurrentWeather, parseHourlyWeather, describeWeather } from "@/services/weather";
import { workerService, type WorkerSiteResponse } from "@safers/api";
import type { ParsedWeather, HourlyWeather } from "@/services/weather.types";

/**
 * 근로자용 현장 페이지 (#102/#103/#112).
 *
 * 현장 UUID(QR)로 진입하는 비인증 경량 페이지.
 * - GET /workers/sites/{uuid} · weather 로 현장정보·날씨 조회 (read-only)
 * - "알림 받기" 시 구독을 현장(uuid)에 귀속 (usePushSubscription)
 *
 * PWA: worker 앱은 자체 manifest(start_url=/worker)라 동적 교체 불필요.
 * 현장(uuid)은 localStorage 로 기억 → 설치 앱은 마지막 현장으로 시작.
 * 현장 이동 = 새 QR → uuid 갱신 + 구독 자동 재등록(usePushSubscription 내부).
 */

const STORAGE_KEY = "worker-site-uuid";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isValidSiteUuid = (v: string | null | undefined): v is string => !!v && UUID_RE.test(v);

export function WorkerPage() {
  const { uuid: uuidParam } = useParams();
  // URL(QR)의 현장이 우선, 없으면 마지막에 저장한 현장(localStorage).
  const storedUuid = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  const uuid = uuidParam ?? storedUuid ?? "";
  const validUuid = isValidSiteUuid(uuid);

  const { busy: subscribeBusy, subscribe } = usePushSubscription(validUuid ? uuid : "");
  const { notices, banner, dismissBanner } = useWorkerNotices(validUuid ? uuid : "");

  // 설치·알림 온보딩. 브라우저 정규화 → 설치 → 알림 권한 순으로 단계가 나온다.
  // pushProbe 로 실제 토큰 발급까지 확인해 GMS 미탑재 기기를 걸러낸다. (#176)
  const {
    step: onboardingStep,
    env,
    busy: onboardingBusy,
    appInstalled,
  } = useOnboarding({ pushProbe: validUuid ? subscribe : undefined });
  const { dismissed: onboardingDismissed, dismiss: dismissOnboarding } = useDismissible(
    "worker-onboarding-dismissed"
  );

  const [site, setSite] = useState<WorkerSiteResponse | null>(null);
  const [siteError, setSiteError] = useState(false);
  const [currentWeather, setCurrentWeather] = useState<ParsedWeather | null>(null);
  const [hourlyWeather, setHourlyWeather] = useState<HourlyWeather[]>([]);

  const loading = validUuid && !site && !siteError;
  const showOnboarding = validUuid && !onboardingDismissed;

  const weatherDesc = describeWeather(currentWeather?.pty ?? null, currentWeather?.sky ?? null);
  const latestNotice = notices[0] ?? null;

  // QR(URL param)로 진입하면 현장을 기억한다. (현장 이동 = 새 QR → 여기서 갱신)
  useEffect(() => {
    if (isValidSiteUuid(uuidParam)) localStorage.setItem(STORAGE_KEY, uuidParam);
    else if (storedUuid && !isValidSiteUuid(storedUuid)) localStorage.removeItem(STORAGE_KEY);
  }, [uuidParam, storedUuid]);

  /*
    현장이 바뀌면 앞 현장의 오류 표시를 즉시 지운다. effect 가 아니라 **렌더 중에
    조정**한다 — effect 는 그린 뒤에 돌아, 새 현장을 받아오는 동안 앞 현장의
    "현장을 찾을 수 없습니다" 가 한 프레임 스쳐 보인다.
  */
  const [prevUuid, setPrevUuid] = useState(uuid);
  if (prevUuid !== uuid) {
    setPrevUuid(uuid);
    setSiteError(false);
  }

  // 현장 정보 + 날씨 조회
  useEffect(() => {
    if (!validUuid) return;
    let cancelled = false;
    workerService
      .getWorkerSite(uuid)
      .then((s) => {
        if (cancelled) return;
        if (s) {
          setSite(s);
          setSiteError(false);
        } else {
          setSite(null);
          setSiteError(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSite(null);
          setSiteError(true);
        }
      });
    workerService
      .getWorkerWeather(uuid)
      .then((groups) => {
        if (cancelled) return;
        setCurrentWeather(parseCurrentWeather(groups));
        setHourlyWeather(parseHourlyWeather(groups));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [uuid, validUuid]);

  if (!validUuid) {
    return (
      <SiteUnavailableScreen
        title="현장 정보가 없습니다."
        description={
          <>
            홈 화면 아이콘을 삭제하고
            <br />
            현장 입구의 QR 코드를 다시 스캔해 주세요.
          </>
        }
      />
    );
  }

  if (siteError && !site) {
    return (
      <SiteUnavailableScreen
        title="현장 정보를 불러올 수 없습니다."
        description={
          <>
            홈 화면 아이콘을 삭제하고
            <br />
            현장 입구의 QR 코드를 다시 스캔하거나
            <br />
            잠시 후 다시 시도해 주세요.
          </>
        }
      />
    );
  }

  // 앱이 설치돼 있는데 브라우저로 들어왔다면 조회를 막고 앱으로 넘긴다.
  // 알림 권한이 아직이면 여기서 먼저 받아 앱이 물려받게 한다.
  if (
    appInstalled &&
    !env.isStandalone &&
    (onboardingStep.kind === "open-app" || onboardingStep.kind === "enable-notification")
  ) {
    return <AppHandoffScreen step={onboardingStep} busy={onboardingBusy || subscribeBusy} />;
  }

  if (loading) return <WorkerSkeleton />;

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col gap-4 bg-slate-100 p-4 pt-[calc(1rem_+_env(safe-area-inset-top))]">
      {/* 헤더 — QR 로 들어온 근로자가 현장을 먼저 확인해야 하므로 온보딩보다 위에 둔다 */}
      <header className="rounded-2xl bg-brand p-4 text-white">
        <div className="text-xs opacity-80">SAFERS 현장 안전정보</div>
        <div className="mt-1 text-lg font-bold">{site?.name ?? "현장"}</div>
      </header>

      {showOnboarding && (
        <OnboardingCard
          step={onboardingStep}
          env={env}
          busy={onboardingBusy || subscribeBusy}
          onDismiss={dismissOnboarding}
        />
      )}

      {/* 방금 도착한 관리자 안내 배너 (#171) */}
      {banner && (
        <section className="rounded-2xl border-2 border-brand bg-white p-4 shadow-sm">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-brand/10 text-xs font-extrabold text-brand">
              !
            </span>
            <span className="text-xs font-bold text-brand">{banner.title}</span>
            <span className="ml-auto text-[0.6875rem] text-neutral-400">방금</span>
          </div>
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-neutral-800">
            {banner.message}
          </p>
          <button
            type="button"
            onClick={dismissBanner}
            className="mt-3 w-full rounded-xl bg-brand py-2.5 text-sm font-semibold text-white"
          >
            확인
          </button>
        </section>
      )}

      {/* 최근 받은 안내 (#171) — 최신 1건 */}
      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="mb-3 text-sm font-semibold text-neutral-500">최근 안내</div>
        {latestNotice ? (
          <>
            <p className="whitespace-pre-wrap break-words text-base leading-relaxed text-neutral-800">
              {latestNotice.message}
            </p>
            <div className="mt-2 text-xs text-neutral-400">
              {formatNoticeTime(latestNotice.receivedAt)}
            </div>
          </>
        ) : (
          <p className="py-2 text-center text-xs leading-relaxed text-neutral-400">
            아직 받은 안내가 없습니다.
            <br />
            현장 관리자가 보낸 안내가 여기에 표시됩니다.
          </p>
        )}
      </section>

      {/* 날씨 카드 */}
      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="mb-3 text-sm font-semibold text-neutral-500">현재 날씨</div>
        <div className="flex items-center gap-4">
          <img src={weatherDesc.src} alt={weatherDesc.label} className="h-14 w-14 flex-none" />
          <div className="flex items-end gap-1">
            <span className="text-5xl font-bold text-neutral-800">
              {currentWeather?.temperature ?? "--"}
            </span>
            <span className="pb-2 text-xl text-neutral-400">°C</span>
          </div>
          {weatherDesc.label && (
            <span className="ml-auto rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-neutral-600">
              {weatherDesc.label}
            </span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {currentWeather?.heatIndex && (
            <Metric label="체감" value={`${currentWeather.heatIndex}°`} />
          )}
          {currentWeather?.humidity && (
            <Metric label="습도" value={`${currentWeather.humidity}%`} />
          )}
          {currentWeather?.windSpeed && (
            <Metric
              label="바람"
              value={`${currentWeather.windDirection ?? ""} ${currentWeather.windSpeed}m/s`.trim()}
            />
          )}
          {currentWeather?.rainfall && currentWeather.rainfall !== "강수없음" && (
            <Metric label="강수" value={currentWeather.rainfall} />
          )}
        </div>

        {/* 시간별 */}
        <div className="mt-4 flex justify-between gap-1 overflow-x-auto">
          {hourlyWeather.map((h) => {
            const d = describeWeather(h.pty, h.sky, Number(h.hour));
            return (
              <div
                key={h.hour}
                className={`flex min-w-[2.75rem] flex-col items-center rounded-lg px-1 py-2 ${
                  h.isCurrent ? "bg-brand/10 font-semibold text-brand" : "text-neutral-500"
                }`}
              >
                <span className="text-xs">{h.isCurrent ? "지금" : `${h.hour}시`}</span>
                <img src={d.src} alt={d.label} className="mt-1 h-6 w-6" />
                <span className="mt-1 text-sm">{h.temp}°</span>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function WorkerSkeleton() {
  return (
    <main className="mx-auto flex min-h-screen max-w-md animate-pulse flex-col gap-4 bg-slate-100 p-4">
      <div className="rounded-2xl bg-brand/60 p-4">
        <div className="h-3 w-24 rounded bg-white/40" />
        <div className="mt-2 h-5 w-40 rounded bg-white/50" />
      </div>
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="h-3 w-20 rounded bg-neutral-200" />
        <div className="mt-4 flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-neutral-200" />
          <div className="h-10 w-24 rounded bg-neutral-200" />
        </div>
        <div className="mt-4 flex gap-2">
          <div className="h-7 w-20 rounded-lg bg-neutral-100" />
          <div className="h-7 w-24 rounded-lg bg-neutral-100" />
        </div>
      </div>
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="h-3 w-16 rounded bg-neutral-200" />
        <div className="mt-3 h-4 w-full rounded bg-neutral-200" />
        <div className="mt-2 h-4 w-2/3 rounded bg-neutral-200" />
      </div>
    </main>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span className="rounded-lg bg-slate-50 px-3 py-1.5 text-sm text-neutral-700">
      <span className="text-neutral-400">{label}</span> {value}
    </span>
  );
}

/** 안내 수신 일시 표기 — 오늘이면 "HH:mm", 이전이면 "M/D HH:mm". */
function formatNoticeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay ? `${hh}:${mm}` : `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`;
}
